'use client'

import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import {
  Bars3Icon,
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  EnvelopeIcon,
  HomeIcon,
  LanguageIcon,
  ListBulletIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserGroupIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/16/solid'
import clsx from 'clsx/lite'
import { Resources } from 'i18next'
import Cookies from 'js-cookie'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'

import { useEffect, useState } from 'react'

import { Lang, locales, replaceLang } from '@/i18n'
import { appendUTMToUrl } from '@/lib/utm'

interface NavigationProps {
  lang: Lang
  resources: Resources
}

const languageCode: Record<Lang, string> = {
  bs: 'BHS',
  en: 'EN',
}

const languageFlag: Record<Lang, string> = {
  bs: '/lang/ba.svg',
  en: '/lang/en.png',
}

const blogPosts = [
  { label: 'Navigation.Blog.Excursion', slug: 'ekskurzija-spanija-italija-2026' },
  { label: 'Navigation.Blog.Students', slug: 'nakon-podrske-bh-navijacima-esimfly-poklanja-besplatan-internet-studentima' },
  { label: 'Navigation.Blog.Fans', slug: 'domaci-startup-esimfly-poklanja-besplatan-internet-bh-navijacima-amerika-kanada' },
]

function checkIsMobileRedirect() {
  return typeof window !== 'undefined' && window.location.search.includes('mobile')
}

function pathWithoutLang(pathname: string) {
  const stripped = pathname.replace(/^\/(bs|en)(?=\/|$)/, '').replace(/\/$/, '')
  return stripped === '' ? '/' : stripped
}

function isActive(pathname: string, href: string) {
  const path = pathWithoutLang(pathname)
  const target =
    href
      .split('#')[0]
      .replace(/^\/(bs|en)(?=\/|$)/, '')
      .replace(/\/$/, '') || '/'
  if (target === '/') return path === '/'
  return path === target || path.startsWith(`${target}/`)
}

function withUtm(url: string) {
  const hashIndex = url.indexOf('#')
  const hash = hashIndex === -1 ? '' : url.slice(hashIndex)
  const path = hashIndex === -1 ? url : url.slice(0, hashIndex)
  return `${appendUTMToUrl(path)}${hash}`
}

function barLinkClass(active?: boolean) {
  return clsx(
    'rounded-full px-2.5 py-1.5 text-[13px] font-medium tracking-tight transition-colors xl:px-3 xl:text-sm',
    active ? 'bg-white text-blue-950' : 'text-white/85 hover:bg-white/10 hover:text-white',
  )
}

function AppleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox='0 0 24 24' className={className} fill='currentColor' aria-hidden='true'>
      <path d='M16.37 1.43c0 1.14-.5 2.27-1.18 3.08-.74.9-1.99 1.57-2.99 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.57-2.27 1.2-2.98.81-.94 2.15-1.64 3.25-1.68.03.13.06.28.06.43zm4.57 15.71c-.03.07-.47 1.58-1.52 3.12-.95 1.37-1.93 2.73-3.48 2.76-1.54.03-2.04-.91-3.8-.91-1.77 0-2.32.88-3.78.94-1.49.06-2.63-1.48-3.58-2.84-1.94-2.79-3.43-7.88-1.43-11.32.99-1.71 2.76-2.79 4.68-2.82 1.47-.03 2.85.98 3.75.98.89 0 2.57-1.21 4.34-1.03.74.03 2.82.3 4.16 2.25-.11.07-2.48 1.45-2.45 4.32.03 3.43 3.01 4.57 3.05 4.59z' />
    </svg>
  )
}

function AndroidIcon({ className }: { className?: string }) {
  return (
    <svg viewBox='0 0 24 24' className={className} fill='currentColor' aria-hidden='true'>
      <path d='M17.6 9.48l1.84-3.18a.637.637 0 0 0-.26-.85.637.637 0 0 0-.83.22l-1.88 3.24a11.46 11.46 0 0 0-8.94 0L5.65 5.67a.643.643 0 0 0-.87-.2.643.643 0 0 0-.22.83L6.4 9.48A10.78 10.78 0 0 0 1 18h22a10.78 10.78 0 0 0-5.4-8.52zM7 15.25a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5zm10 0a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5z' />
    </svg>
  )
}

function WhatsAppIcon() {
  return (
    <svg viewBox='0 0 24 24' className='size-6 shrink-0' aria-hidden='true'>
      <path
        fill='currentColor'
        d='M12.04 2C6.58 2 2.15 6.4 2.15 11.83c0 1.74.46 3.44 1.34 4.94L2 22l5.39-1.41a10 10 0 0 0 4.65 1.18h.01c5.46 0 9.89-4.4 9.89-9.83C21.94 6.4 17.5 2 12.04 2Zm5.76 13.94c-.24.68-1.4 1.3-1.94 1.38-.5.08-1.12.11-1.81-.11-.42-.14-.95-.31-1.64-.6-2.88-1.24-4.76-4.14-4.9-4.33-.14-.2-1.16-1.54-1.16-2.94 0-1.4.73-2.09 1-2.37.26-.28.57-.35.76-.35h.55c.17 0 .41-.06.64.49.24.57.81 1.97.88 2.11.07.14.12.31.02.5-.1.2-.14.31-.28.48-.14.17-.3.37-.42.5-.14.14-.29.29-.12.56.17.28.74 1.22 1.59 1.98 1.09.97 2.01 1.27 2.3 1.42.28.14.45.12.62-.07.17-.2.71-.82.9-1.1.19-.28.38-.23.64-.14.26.1 1.66.78 1.94.92.28.14.47.21.54.33.07.12.07.68-.17 1.36Z'
      />
    </svg>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className='mx-2 mb-1 mt-2 border-t border-slate-100 px-2 pt-3'>
      <p className='text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400'>{children}</p>
    </div>
  )
}

const Navigation = ({ lang, resources }: NavigationProps) => {
  const pathname = usePathname()
  const [hide, setHide] = useState(false)
  const [open, setOpen] = useState(false)
  const [installOpen, setInstallOpen] = useState(true)

  useEffect(() => {
    if (checkIsMobileRedirect()) {
      setHide(true)
    }
  }, [])

  useEffect(() => {
    setOpen(false)
  }, [pathname])

  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)')
    const onChange = () => {
      if (media.matches) setOpen(false)
    }
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    if (!open) return
    setInstallOpen(true)
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const close = () => setOpen(false)

  const homeHref = `/${lang}`
  const blogHref = `/${lang}/blogs`
  const faqHref = `/${lang}#faq-heading`
  const aboutHref = `/${lang}/about-us`
  const contactHref = `/${lang}/contact-us`
  const payHref = `/${lang}/payment-info`
  const securityHref = `/${lang}/payment-security`
  const guideHref = `/${lang}/installation-guide`
  const iosHref = `/${lang}/installation-guide?tab=ios`
  const samsungHref = `/${lang}/installation-guide?tab=samsung`
  const reviewsHref = `/${lang}#reviews-heading`
  const whyHref = `/${lang}#why-esimfly-heading`
  const howHref = `/${lang}#how-it-works-title`

  const posts = blogPosts.map(post => ({
    href: `/${lang}/blogs/${post.slug}`,
    label: resources[post.label] as string,
  }))

  function scrollToSection(event: React.MouseEvent<HTMLAnchorElement>, id: string) {
    close()
    if (pathWithoutLang(pathname) !== '/') return
    event.preventDefault()
    window.requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  const guideActive = isActive(pathname, guideHref)
  const blogActive = isActive(pathname, blogHref)
  const paymentActive = isActive(pathname, payHref) || isActive(pathname, securityHref)

  return (
    <nav className={clsx('sticky top-0 z-50', hide && 'hidden')}>
      <div className={clsx('relative z-50', open ? 'bg-white' : 'bg-grayDark-400/20 backdrop-blur-md')}>
        <div className='mx-auto max-w-[1440px] px-3 pb-2 pt-3 sm:px-4'>
          <div className='relative z-50 flex h-14 items-center gap-2 rounded-[22px] bg-blue-950 px-2.5 shadow-[0_10px_28px_-16px_rgba(36,45,82,0.9)] sm:gap-3 sm:px-3'>
            <button
              type='button'
              className='inline-flex size-9 shrink-0 items-center justify-center rounded-full border border-white/80 text-white transition-colors hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 lg:hidden'
              aria-expanded={open}
              aria-controls='site-navigation'
              onClick={() => setOpen(value => !value)}
            >
              <span className='sr-only'>{open ? resources['Navigation.Action.CloseMenu'] : resources['Navigation.Action.OpenMenu']}</span>
              {open ? <XMarkIcon className='size-5' aria-hidden='true' /> : <Bars3Icon className='size-5' aria-hidden='true' />}
            </button>

            <Link prefetch={false} href={withUtm(homeHref)} onClick={close} className='flex shrink-0 items-center'>
              <Image className='h-[26px] w-auto lg:h-7' height={213} width={972} src='/logo-nav.png' alt='eSIMFly' />
            </Link>

            <div className='hidden min-w-0 flex-1 items-center justify-center gap-x-3 lg:flex'>
              <Link
                prefetch={false}
                href={withUtm(homeHref)}
                aria-current={isActive(pathname, homeHref) ? 'page' : undefined}
                className={clsx(barLinkClass(isActive(pathname, homeHref)), 'whitespace-nowrap')}
              >
                {resources['Navigation.Action.Landing']}
              </Link>

              <BarMenu label={resources['Navigation.Action.Blog']} active={blogActive} wide items={posts.map(post => ({ ...post, href: withUtm(post.href), active: isActive(pathname, post.href) }))} />

              <BarMenu
                label={
                  <>
                    <span className='xl:hidden'>{resources['Navigation.Menu.GuideShort']}</span>
                    <span className='hidden xl:inline'>{resources['Navigation.Action.HowToInstall']}</span>
                  </>
                }
                active={guideActive}
                items={[
                  { href: withUtm(iosHref), label: resources['Navigation.Menu.Ios'] as string },
                  { href: withUtm(samsungHref), label: resources['Navigation.Menu.Samsung'] as string },
                ]}
              />

              <Link prefetch={false} href={withUtm(faqHref)} onClick={event => scrollToSection(event, 'faq-heading')} className={clsx(barLinkClass(false), 'whitespace-nowrap')}>
                <span className='xl:hidden'>{resources['Navigation.Menu.FaqShort']}</span>
                <span className='hidden xl:inline'>{resources['Navigation.Menu.Faq']}</span>
              </Link>

              <Link
                prefetch={false}
                href={withUtm(aboutHref)}
                aria-current={isActive(pathname, aboutHref) ? 'page' : undefined}
                className={clsx(barLinkClass(isActive(pathname, aboutHref)), 'whitespace-nowrap')}
              >
                {resources['Navigation.Action.AboutUs']}
              </Link>
              <Link
                prefetch={false}
                href={withUtm(contactHref)}
                aria-current={isActive(pathname, contactHref) ? 'page' : undefined}
                className={clsx(barLinkClass(isActive(pathname, contactHref)), 'whitespace-nowrap')}
              >
                {resources['Navigation.Menu.Contact']}
              </Link>

              <BarMenu
                label={resources['Navigation.Action.Payment']}
                active={paymentActive}
                items={[
                  { href: withUtm(payHref), label: resources['Navigation.Action.HowToPay'] as string, active: isActive(pathname, payHref) },
                  { href: withUtm(securityHref), label: resources['Navigation.Action.PaymentSecurity'] as string, active: isActive(pathname, securityHref) },
                ]}
              />
            </div>

            <div className='ml-auto flex shrink-0 items-center gap-1 sm:gap-2 lg:ml-0'>
              <Menu as='div' className='relative'>
                {({ open: languageOpen }) => (
                  <>
                    <MenuButton className='group inline-flex items-center gap-1 rounded-full px-1.5 py-1.5 text-sm font-normal tracking-tight text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70'>
                      <span className='sr-only'>{resources['Navigation.Action.ChangeLanguage']}</span>
                      <LanguageIcon className='size-5' aria-hidden='true' />
                      {languageCode[lang]}
                      <ChevronDownIcon className={clsx('size-4 text-white/80 transition-transform duration-150', languageOpen && 'rotate-180')} aria-hidden='true' />
                    </MenuButton>
                    {languageOpen && (
                      <MenuItems static className='absolute right-0 z-[60] mt-2 w-44 origin-top-right rounded-xl bg-white p-1.5 shadow-lg ring-1 ring-blue-950/10 focus:outline-none'>
                        {locales.map(locale => {
                          const href = withUtm(replaceLang({ pathname, locale }))
                          const current = locale === lang
                          return (
                            <MenuItem key={locale}>
                              {({ focus }) => (
                                <Link
                                  href={href}
                                  prefetch={false}
                                  onClick={() => {
                                    Cookies.set('NEXT_LOCALE', locale)
                                    close()
                                  }}
                                  className={clsx(
                                    'flex items-center gap-2 rounded-lg px-3 py-2 text-sm tracking-tight',
                                    focus ? 'bg-slate-50 text-blue-950' : 'text-slate-700',
                                    current && 'font-semibold text-blue-950',
                                  )}
                                >
                                  <img src={languageFlag[locale]} alt='' width={20} height={20} className='size-5 rounded-full' />
                                  <span className='min-w-0 flex-1'>{resources[`Navigation.Text.${locale}Long`]}</span>
                                  {current && <CheckIcon className='size-4 shrink-0 text-red-500' aria-hidden='true' />}
                                </Link>
                              )}
                            </MenuItem>
                          )
                        })}
                      </MenuItems>
                    )}
                  </>
                )}
              </Menu>
            </div>
          </div>
        </div>
      </div>

      {open && (
        <div id='site-navigation' className='fixed inset-0 z-40 overflow-y-auto overscroll-contain bg-white px-3 pb-8 pt-[4.75rem] lg:hidden'>
          <a
            href='https://wa.me/38761603225'
            data-track-cta='whatsapp_navigation'
            target='_blank'
            rel='noopener noreferrer'
            onClick={close}
            className='mb-3 mt-1 flex items-center gap-3 rounded-2xl bg-[#f2fbf5] px-3.5 py-3 text-blue-950 shadow-sm transition-colors hover:bg-[#e4f6eb]'
          >
            <span className='flex size-11 shrink-0 items-center justify-center rounded-xl bg-[#25D366] text-white'>
              <WhatsAppIcon />
            </span>
            <span className='min-w-0 flex-1'>
              <span className='block text-[15px] font-semibold leading-tight'>{resources['Navigation.Menu.WhatsApp']}</span>
              <span className='mt-0.5 block text-[13px] leading-snug text-blue-950/60'>{resources['Navigation.Menu.WhatsAppSubtitle']}</span>
            </span>
            <ChevronRightIcon className='size-4 mr-1 shrink-0 text-blue-950/40' aria-hidden='true' />
          </a>
          <div className='px-2'>
            <NavLink href={withUtm(homeHref)} icon={HomeIcon} label={resources['Navigation.Action.Landing']} active={isActive(pathname, homeHref)} chevron={false} onClick={close} />
            <NavLink href={withUtm(reviewsHref)} icon={ChatBubbleLeftRightIcon} label={resources['Navigation.Menu.Reviews']} onClick={event => scrollToSection(event, 'reviews-heading')} />
            <NavLink href={withUtm(whyHref)} icon={SparklesIcon} label={resources['Navigation.Menu.Why']} wrap onClick={event => scrollToSection(event, 'why-esimfly-heading')} />
            <NavLink href={withUtm(howHref)} icon={ListBulletIcon} label={resources['Navigation.Menu.How']} onClick={event => scrollToSection(event, 'how-it-works-title')} />
          </div>

          <SectionLabel>{resources['Navigation.Menu.Help']}</SectionLabel>
          <div className='px-2'>
            {/* highlighted so customers find the install guides */}
            <div className='my-1 rounded-2xl bg-sky-50 p-1.5 ring-1 ring-inset ring-sky-100'>
              <button
                type='button'
                className='flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left text-[15px] font-semibold tracking-tight text-blue-950 transition-colors hover:bg-sky-100'
                aria-expanded={installOpen}
                aria-controls='nav-install-guides'
                onClick={() => setInstallOpen(value => !value)}
              >
                <span className='flex size-8 shrink-0 items-center justify-center rounded-lg bg-sky-500 text-white'>
                  <Cog6ToothIcon className='size-5' aria-hidden='true' />
                </span>
                <span className='min-w-0 flex-1'>{resources['Navigation.Action.HowToInstall']}</span>
                <ChevronDownIcon className={clsx('size-4 shrink-0 text-sky-700 transition-transform', installOpen && 'rotate-180')} aria-hidden='true' />
              </button>
              {installOpen && (
                <div id='nav-install-guides' className='mt-1.5 grid grid-cols-2 gap-1.5'>
                  <GuideLink href={withUtm(iosHref)} icon={<AppleIcon className='size-5' />} label={resources['Navigation.Menu.Ios']} onClick={close} />
                  <GuideLink href={withUtm(samsungHref)} icon={<AndroidIcon className='size-5' />} label={resources['Navigation.Menu.Samsung']} onClick={close} />
                </div>
              )}
            </div>
            <NavLink href={withUtm(faqHref)} icon={QuestionMarkCircleIcon} label={resources['Navigation.Menu.Faq']} onClick={event => scrollToSection(event, 'faq-heading')} />
          </div>

          <SectionLabel>{resources['Navigation.Action.Blog']}</SectionLabel>
          <div className='px-2'>
            {posts.map(post => (
              <NavLink key={post.href} href={withUtm(post.href)} icon={BookOpenIcon} label={post.label} active={isActive(pathname, post.href)} wrap onClick={close} />
            ))}
          </div>

          <SectionLabel>{resources['Navigation.Action.AboutUs']}</SectionLabel>
          <div className='px-2'>
            <NavLink href={withUtm(aboutHref)} icon={UserGroupIcon} label={resources['Navigation.Action.AboutUs']} active={isActive(pathname, aboutHref)} onClick={close} />
            <NavLink href={withUtm(contactHref)} icon={EnvelopeIcon} label={resources['Navigation.Menu.Contact']} active={isActive(pathname, contactHref)} onClick={close} />
          </div>

          <SectionLabel>{resources['Navigation.Action.Payment']}</SectionLabel>
          <div className='px-2 pb-1'>
            <NavLink href={withUtm(payHref)} icon={CreditCardIcon} label={resources['Navigation.Action.HowToPay']} active={isActive(pathname, payHref)} onClick={close} />
            <NavLink href={withUtm(securityHref)} icon={ShieldCheckIcon} label={resources['Navigation.Action.PaymentSecurity']} active={isActive(pathname, securityHref)} onClick={close} />
          </div>
        </div>
      )}
    </nav>
  )
}

function BarMenu({ label, active, wide, items }: { label: React.ReactNode; active?: boolean; wide?: boolean; items: { href: string; label: string; external?: boolean; active?: boolean }[] }) {
  return (
    <Menu as='div' className='relative'>
      <MenuButton className={clsx(barLinkClass(active), 'group inline-flex items-center gap-1 whitespace-nowrap', !active && 'data-[open]:bg-white/10 data-[open]:text-white')}>
        {label}
        <ChevronDownIcon className='size-4 opacity-70 transition-transform duration-150 group-data-[open]:rotate-180' aria-hidden='true' />
      </MenuButton>
      <MenuItems
        transition
        className={clsx(
          'absolute left-0 z-[60] mt-3 origin-top-left rounded-xl bg-white p-1.5 shadow-lg ring-1 ring-blue-950/10 transition duration-100 ease-out focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0',
          wide ? 'w-80' : 'w-60',
        )}
      >
        {items.map(item => (
          <MenuItem key={item.href}>
            {({ focus }) => {
              const className = clsx(
                'block rounded-lg px-3 py-2 text-sm leading-snug tracking-tight',
                item.active ? 'bg-blue-950/[0.06] font-semibold text-blue-950' : 'text-slate-700',
                focus && !item.active && 'bg-slate-50 text-blue-950',
              )
              if (item.external) {
                return (
                  <a href={item.href} className={className}>
                    {item.label}
                  </a>
                )
              }
              return (
                <Link prefetch={false} href={item.href} className={className}>
                  {item.label}
                </Link>
              )
            }}
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  )
}

function NavLink({
  href,
  icon: Icon,
  label,
  active = false,
  chevron = true,
  wrap = false,
  onClick,
}: {
  href: string
  icon: React.ComponentType<{ className?: string }>
  label: string
  active?: boolean
  chevron?: boolean
  wrap?: boolean
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void
}) {
  return (
    <Link
      prefetch={false}
      href={href}
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={clsx(
        'flex gap-3 rounded-xl px-3 py-2.5 text-[15px] tracking-tight text-blue-950 transition-colors hover:bg-slate-50',
        wrap ? 'items-start' : 'items-center',
        active ? 'font-semibold' : 'font-medium',
      )}
    >
      <Icon className={clsx('size-5 shrink-0', wrap && 'mt-0.5')} aria-hidden='true' />
      <span className={clsx('min-w-0 flex-1', wrap && 'leading-snug')}>{label}</span>
      {chevron && <ChevronRightIcon className={clsx('size-4 shrink-0 text-slate-400', wrap && 'mt-1')} aria-hidden='true' />}
    </Link>
  )
}

function GuideLink({ href, icon, label, onClick }: { href: string; icon: React.ReactNode; label: string; onClick?: () => void }) {
  return (
    <Link
      prefetch={false}
      href={href}
      onClick={onClick}
      className='flex items-center gap-2 rounded-xl bg-white px-3 py-2.5 text-sm font-semibold tracking-tight text-blue-950 shadow-sm ring-1 ring-inset ring-sky-100 transition-colors hover:bg-sky-100'
    >
      <span className='shrink-0 text-sky-600'>{icon}</span>
      <span className='min-w-0 flex-1 leading-tight'>{label}</span>
    </Link>
  )
}

export default Navigation
